/// <amd-module name="typeahead/core/ABTest" />
/// <amd-dependency path="jquery" name="$"/>
/// <amd-dependency path="uitk" name="uitk"/>

/// <reference path="../../typings/typeahead/uitk.d.ts" />
/// <reference path="../../typings/typeahead/dctk.d.ts" />
/// <reference path="../../typings/typeahead/ABTest.d.ts" />

import {Experiment} from "Experiment";
import {IExperimentalFeature} from "typeahead/core/IExperimentalFeature";
import {ProblemOrigin} from "typeahead/core/logging/ProblemOrigin";
import {Severity} from "typeahead/core/logging/Severity";
import TypeaheadErrorLogger from "typeahead/core/logging/TypeaheadErrorLogger";
import {DefaultPluginAssembly} from "typeahead/core/plugins/DefaultPluginAssembly";
import {SiteParam, TypeaheadUtils} from "typeahead/core/TypeaheadUtils";
import {IABTest} from "typings/typeahead/ABTest";

export interface IDctkParamsForUta {
    readonly guid: string;
    readonly tpid: number;
    readonly eapid: number;
    readonly name: string;
    readonly logToOmniture: boolean;
    readonly overrides: string;
    readonly callback: IDctkCallback;
}

interface IAbacusParams {
    readonly guid: string;
    readonly tpid: number;
    readonly eapid: number;
    readonly verbose: boolean;
    readonly id?: string[];
    readonly name?: string[];
    readonly overrides: string;
}

interface IAbacusResponse {
    evaluatedExperiments: IEvaluatedExperiment[];
}

interface IEvaluatedExperiment {
    instanceId: number;
    id: number;
    name: string;
    value: number;
    message: {
        description: string;
        id: number;
        type: string;
    };
}

export interface IErrorCode {
    readonly id: number;
    readonly severity: Severity;
    readonly problemOrigin: ProblemOrigin;
}

// "Unexpected error occured"
let defaultErrorCode: IErrorCode = {
    id: 2000, problemOrigin: ProblemOrigin.DataCaptureToolkit, severity: Severity.Excusable,
};
let errorCodes: IErrorCode[] = [
    defaultErrorCode,
    // Evaluation failures
    // "Error occurred during getting preserve data."
    {id: 50000, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // "Invalid input parameters. {0}"
    {id: 50001, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // "Error occurred during bucketing."
    {id: 50002, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    /* Error occurred during the evaluation of one or more experiments   type: WARN
    This error could occur when the experiment is being evaluated for a site where it is not enabled.*/
    {id: 50003, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Negligible},
    // "The EntityType of the provided experiments must be the same. We have seen {0} and {1}"
    {id: 50004, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // Exposure Logging failures
    // "Request validation failed with: {0}"
    {id: 50100, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // "An error occurred resolving experiment {0} ({1}) for exposure logging"
    {id: 50101, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // "Experiment {0} ({1}) is not in test mode and so cannot be logged"
    {id: 50102, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // "Experiment {0} ({1}) has already been logged"
    {id: 50103, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // "Could not find a treatment group for value {0} in experiment {1}"
    {id: 50104, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // Data reading Failures
    // "Can't read the EntityType of the experiments"
    {id: 70001, problemOrigin: ProblemOrigin.DataCaptureToolkit, severity: Severity.Excusable},
    // "Can't read the run type of the experiments"
    {id: 70002, problemOrigin: ProblemOrigin.DataCaptureToolkit, severity: Severity.Excusable},
    // Site hierarchy
    // "No site found for siteId={0}"*/
    {id: 8000, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Excusable},
    // "The given (TPID, EAPID) pair '(1, 0)' is not valid for the Experiment '13,081'"
    {id: 4301, problemOrigin: ProblemOrigin.UniversalTypeahead, severity: Severity.Negligible},

];

export class ABTest implements IABTest {
    private persistentCookie: IPersistentCookie;
    private abovCookieValue: string;
    private abTestMap: Object;
    private loggedTests: string[];
    private ajaxCallCache: Object;

    /*
    Various components call ExecutionHelper which calls abTest for experiment logging / assignments. Usually assignments
    are obtained when uTA is initialized from getAllExperimentNames() but in some cases we need to obtain the assignment
    from dctk which blocks execution if dctk is not ready.

    This blocking is undesirable so we are making ExecutionHelper.shouldExecute() return a result immediately; if the
    experiment is not already assigned when shouldExecute() is called we essentially ignore it completely by locking it
    to the control bucket in addition to not logging it.

    AbTest maintains the state of the experiments so logically we are putting controlLockedExpMap here. ExecutionHelper
    decides when to execute so it will lock experiments as necessary and treat them appropriately.
     */
    private controlLockedExpMap: Object;

    constructor() {
        this.persistentCookie = uitk.createBrowserStorage ? uitk.createBrowserStorage("persistentCookies") : undefined;
        this.abovCookieValue = TypeaheadUtils.getCookie("abov");
        this.abTestMap = <string & Experiment> {};
        this.loggedTests = [];
        this.ajaxCallCache = <string & JQueryDeferred<Experiment>> {};
        this.controlLockedExpMap = <string & Experiment> {};
    }

    /*
    Gets and Logs the variant of an Abacus Test from its name.
     */
    public getAndLog(expName: string): JQueryPromise<Experiment> {
        if (expName) {
            if (this.hasEvaluatedExp(expName)) {
                this.logExperimentAsynchronously(expName);
                return $.Deferred().resolve(this.abTestMap[expName]).promise();
            }

            // Do not log if test is already logged or previous test evaluation was an error
            if (this.loggedTests.indexOf(expName) < 0
                && !(this.abTestMap[expName] && this.abTestMap[expName].value === undefined)) {
                this.logExperiment(expName);
            }
            return this.get(expName);
        }
        TypeaheadErrorLogger.log(new Error("Experiment name missing when calling 'getAndLog'"),
            Severity.MustFix, ProblemOrigin.UniversalTypeahead);
        return $.Deferred().reject().promise();
    }

    /*
    Overrides the variant of an Abacus ID.

    @param testId: Mandatory - number - The experiment ID
    @param variant: Mandatory - number - the variant number (0=control, 1=variant1, etc)
    */
    public override(testId: number, variant: number): void {
        let overrideValue = [testId, "0", variant].join("|");
        let abov = this.persistentCookie.readItem("abov");
        let newAbov = overrideValue;
        if (abov) {
            newAbov = abov;
            let testIdStr = testId + "";
            let testIdIndex = newAbov.indexOf(testIdStr + "|");
            if (testIdIndex >= 0) {
                let patt = new RegExp(testIdStr + "\\|[0-9]+\\|[0-9]+");
                newAbov = newAbov.replace(patt, overrideValue);
            } else {
                newAbov += ":" + overrideValue;
            }
        }
        this.persistentCookie.saveItem("abov", newAbov);
    }

    /*
     Returns the content of the "abov" cookie - all the overridden experiments for the user on the current domain.
     */
    public getCookieValue(): string {
        return this.abovCookieValue ? this.abovCookieValue : undefined;
    }

    public getActiveClientTests(): string {
        let activeClientTests: string = "";
        for (let i: number = 0; i < this.loggedTests.length; i++) {
            if (this.abTestMap[this.loggedTests[i]] && this.abTestMap[this.loggedTests[i]].id) {
                activeClientTests += this.abTestMap[this.loggedTests[i]].id + "."
                    + this.abTestMap[this.loggedTests[i]].value + "|";
            }
        }
        return activeClientTests ? activeClientTests.slice(0, -1) : activeClientTests;
    }

    public getABTestMap() {
        return this.abTestMap;
    }

    public lockExperimentToControl(experiment: Experiment): void {
        experiment.value = 0;
        this.abTestMap[experiment.name] = experiment;
        this.controlLockedExpMap[experiment.name] = experiment;
    }

    public evaluateAllExperiments(plugins: IPluginAssembly): void {
        this.evaluateExperiments(
            this.getAllExperimentNames(plugins.get(DefaultPluginAssembly.experimentIdentifier)));
    }

    public hasEvaluatedExp(expName: string): boolean {
        return this.abTestMap.hasOwnProperty(expName) && this.abTestMap[expName].value !== undefined;
    }

    public hasControlLockedExp(expName: string): boolean {
        return this.controlLockedExpMap.hasOwnProperty(expName);
    }

    public logExperimentAsynchronously(expName: string): void {
        setTimeout(() => {
            this.logExperiment(expName);
        });
    }

    private logExperiment(expName: string): void {
        /*
        The decision of what to do with locked experiments is handled by ExecutionHelper; it should not call loggging
        for a locked experiment so this check isnt strictly necessary but is here for clarity.
         */
        if (!this.hasControlLockedExp(expName)) {
            this.loggedTests.push(expName);
            require(["dctk/dctk"], (dctk) => {
                dctk.evaluateExperimentAndLog(this.getDctkParams(expName, true, undefined));
            });
        }
    }

    private getAllExperimentNames(experimentalFeatures: IExperimentalFeature[]): string[] {
        let experimentNames: string[] = [];
        let getExperimentName = (experiment: Experiment): void => {
            if (experimentNames.indexOf(experiment.name) < 0) {
                experimentNames.push(experiment.name);
            }
        };
        let getExperiments = (experimentalFeature: IExperimentalFeature): void => {
            let experiments: Experiment[] = experimentalFeature.getExperiments();
            if (experiments && experiments.length) {
                experiments.forEach(getExperimentName);
            }
        };

        if (experimentalFeatures && experimentalFeatures.length) {
            experimentalFeatures.forEach(getExperiments);
        }
        return experimentNames;
    }

    private evaluateExperiments(experiments: string[]): void {
        if (experiments && experiments.length) {
            $.ajax({
                contentType: "application/json; charset=UTF-8",
                data: JSON.stringify(this.getAbacusParams(experiments)),
                dataType: "json",
                type: "POST",
                url: "/api/bucketing/v1/evaluateExperiments",
            }).done((response: IAbacusResponse) => {
                if (response && response.evaluatedExperiments && response.evaluatedExperiments.length) {
                    response.evaluatedExperiments.forEach((exp: IEvaluatedExperiment) => {
                        this.abTestMap[exp.name] = new Experiment(exp.name, exp.id,
                            exp.message ? undefined : exp.value);
                    });
                }
            }).fail(() => {
                TypeaheadErrorLogger.log(new Error(
                    "Unable to call abacus api to evaluate experiments at page load"),
                    Severity.Excusable, ProblemOrigin.UniversalTypeahead);
            });
        }
    }

    // Returns true if error is not ignoreable and false if error can be ignored
    private processDctkError(response: any, expName: string, additionMsg?: string): boolean {
        let isError: boolean = true;
        if (response && response.error) {
            let errorCode: IErrorCode;
            let filter: IErrorCode[] = errorCodes.filter((errorCodeToFind: IErrorCode) => {
                let err = (typeof response.error === "string") ? response.error : response.error.toString();
                return err.indexOf(errorCodeToFind.id) > -1;
            });
            errorCode = (filter && filter.length > 0) ? filter[0] : defaultErrorCode;
            if (errorCode.severity !== Severity.Negligible) {
                TypeaheadErrorLogger.log(new Error("Error evaluating experiment [" + expName + "]: "
                    + response.error + (additionMsg ? ", addition: " + additionMsg : "")),
                    errorCode.severity, errorCode.problemOrigin);
            } else {
                isError = false;
            }
        }
        return isError;
    }

    private getDctkParams(expName: string, logToOmniture: boolean, callback: IDctkCallback): IDctkParamsForUta {
        let dctkParams: IDctkParamsForUta = {
            callback,
            eapid: TypeaheadUtils.getSiteParamValue(SiteParam.eapid),
            guid: TypeaheadUtils.getGuid(),
            logToOmniture,
            name: expName,
            overrides: this.getCookieValue(),
            tpid: TypeaheadUtils.getSiteParamValue(SiteParam.tpid),
        };
        return dctkParams;
    }

    private getAbacusParams(experiments: string[]) {
        return {
            eapid: TypeaheadUtils.getSiteParamValue(SiteParam.eapid),
            guid: TypeaheadUtils.getGuid(),
            name: experiments,
            overrides: this.getCookieValue(),
            tpid: TypeaheadUtils.getSiteParamValue(SiteParam.tpid),
            verbose: true,
        };
    }

    private publishTestResult(experiment: Experiment, success: boolean): void {
        let cacheKey = experiment ? experiment.name || "" : "";
        if (this.ajaxCallCache.hasOwnProperty(cacheKey)
            && typeof this.ajaxCallCache[cacheKey] !== "undefined") {
            while (this.ajaxCallCache[cacheKey].length) {
                // first come first served
                let deferred: JQueryDeferred<Experiment> = this.ajaxCallCache[cacheKey].shift();
                // we notify the experiment: either a successful one or a failure one
                if (success) {
                    deferred.resolve(experiment);
                } else {
                    deferred.reject(experiment);
                }
            }
            this.ajaxCallCache[cacheKey] = undefined;
        }
    }

    private evaluateExperimentAsynchronously(expName: string) {
        setTimeout(() => {
            require(["dctk/dctk"], (dctk) => {
                let test: Experiment;
                dctk.evaluateExperiment(this.getDctkParams(expName, false, (experiment) => {
                    if (experiment.error) {
                        test = new Experiment(expName, undefined, undefined);
                        this.abTestMap[test.name] = test;
                        this.publishTestResult(new Experiment(expName),
                            !this.processDctkError(experiment, expName));
                    } else {
                        if (experiment.id) {
                            test = new Experiment(expName, experiment.id, experiment.value);
                            this.abTestMap[test.name] = test;
                        }
                        this.publishTestResult(test, true);
                    }
                }));
            });
        }, 0);
    }

    /*
    To test on localhost, install Charles and add a mapping:
    https://ewegithub.sb.karmalab.net/EWE/datacapture-toolkit/
    blob/master/datacapture/src/main/resources/datacapture/js/docs/testing-changes-with-charles.md
    from http://localhost:8080/api/bucketing/v1/evaluateExperiments to
    https://www.expedia.com/api/bucketing/v1/evaluateExperiments
    from http://localhost:8080/api/bucketing/v1/evaluateExperimentsAndLog to
    https://www.expedia.com/api/bucketing/v1/evaluateExperimentsAndLog

    Gets the variant of an Abacus Test from its name.
    Asynchronous method!
    @param expName: Mandatory - String - The experiment name to evaluate.
    @param successCallback: Mandatory - DoneCallback -
    The callback function called after receiving a successful response from abacus service.
    @param successCallback: Optional - errorCallback -
    The callback function called after receiving an errorful response from abacus service.
    */

    private get(expName: string): JQueryPromise<Experiment> {
        let deferred = $.Deferred();
        if (expName) {
            let test: Experiment;
            let testCached = false;
            if (!!this.abTestMap[expName]) {
                test = this.abTestMap[expName];
                testCached = true;
            }

            if (!testCached) {
                if (this.ajaxCallCache.hasOwnProperty(expName)
                    && typeof this.ajaxCallCache[expName] !== "undefined") {
                    // it is being evaluated, we can just queue it
                    this.ajaxCallCache[expName].push(deferred);
                } else {
                    // first time trying to evaluate this test
                    // we just cache it first
                    this.ajaxCallCache[expName] = [deferred];

                    this.evaluateExperimentAsynchronously(expName);

                }
            } else {
                deferred.resolve(test);
            }
        } else {
            deferred.reject(new Experiment("experiment name must be provided"));
        }
        return deferred.promise();
    }
}
